/**
 * BottomNav — persistent, fixed bottom destination bar.
 *
 * Six slots (Ops only for admins): Agents · Tasks · Files · Ops · Settings · More — each with an
 * SVG icon from the shared set (ComposerIcons.tsx), never an emoji glyph (#2216).
 *   - Agents   → opens the Agents drawer (the session list / SessionListView); inert when omitted.
 *                LEFTMOST entry — the session list is now a drawer, not a routable view
 *                (agents-drawer-carousel-reorg.md Phase 1; coexists with goToList, which still works).
 *   - Tasks    → opens the Tasks drawer (WorkItemsPanel); inert when omitted
 *   - Files    → opens the FileListDrawer
 *   - Ops      → opens the Ops drawer (ops-proxy embed); OMITTED (not just dimmed) when
 *                absent — a non-admin never becomes reachable for it via normal use, so a
 *                permanently-inert slot is pure clutter, not a meaningful affordance
 *                (issue #4, coach-tooltip-system.md Phase 5).
 *   - Settings → opens the Settings modal (moved here from the composer cluster ⚙ button)
 *   - More     → opens the GearMenu (session actions, sign-out, etc.) — Usage now lives here
 *                (issue #4 declutter; the 7-slot row was crowded at 390px).
 *
 * Pure renderer wired to the callbacks App.tsx exposes. Touch targets >=48px (R-013);
 * colours from theme tokens only (R-004).
 */

import type { ComponentChildren } from 'preact';
import { markDiscovered } from '../state/coachStore';
import { AgentsIcon, TasksIcon, FilesIcon, OpsIcon, SettingsIcon, MoreIcon } from './ComposerIcons';

interface BottomNavProps {
  /** Opens the Agents drawer (SessionListView) — new leftmost slot. Inert when omitted. */
  onAgents?: () => void;
  onFiles: () => void;
  onMore: () => void;
  /** Opens the Tasks drawer (WorkItemsPanel). When omitted the slot stays inert. */
  onTasks?: () => void;
  /** Opens the Ops drawer (ops-proxy embed). When omitted the slot is not rendered at all. */
  onOps?: () => void;
  /** Opens the Settings modal — moved from the composer cluster ⚙ button. */
  onSettings?: () => void;
}

interface Slot {
  id: string;
  icon: ComponentChildren;
  label: string;
  onTap?: () => void;
  /** Inert placeholder — rendered but dimmed and non-interactive. */
  inert?: boolean;
}

export function BottomNav({ onAgents, onFiles, onMore, onTasks, onOps, onSettings }: BottomNavProps) {
  const slots: Slot[] = [
    { id: 'agents', icon: <AgentsIcon />, label: 'Agents', onTap: onAgents, inert: !onAgents },
    { id: 'tasks', icon: <TasksIcon />, label: 'Tasks', onTap: onTasks, inert: !onTasks },
    { id: 'files', icon: <FilesIcon />, label: 'Files', onTap: onFiles },
    ...(onOps ? [{ id: 'ops', icon: <OpsIcon />, label: 'Ops', onTap: onOps }] : []),
    { id: 'settings', icon: <SettingsIcon />, label: 'Settings', onTap: onSettings },
    { id: 'more', icon: <MoreIcon />, label: 'More', onTap: onMore },
  ];

  return (
    <nav class="bottom-nav" aria-label="Primary">
      {slots.map((slot) => (
        <button
          key={slot.id}
          type="button"
          class={`bottom-nav-slot${slot.inert ? ' bottom-nav-slot--inert' : ''}`}
          {...(slot.id === 'agents' ? { 'data-coach-id': 'agents-nav' } : {})}
          onClick={slot.inert ? undefined : () => {
            // Real usage of Agents nav = discovery of "your chats live under Agents"
            // (coach-tooltip-system.md §5.2) — cancel the tip mid-display + mark seen.
            if (slot.id === 'agents') markDiscovered('agents-nav');
            slot.onTap?.();
          }}
          disabled={slot.inert}
          title={slot.inert ? `${slot.label} (coming soon)` : slot.label}
          aria-label={slot.label}
        >
          <span class="bottom-nav-icon" aria-hidden="true">{slot.icon}</span>
          <span class="bottom-nav-label">{slot.label}</span>
        </button>
      ))}
    </nav>
  );
}
